import { useEffect, useRef, useState } from 'react';

import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import SidebarDemoBadge from '@/components/SidebarDemoBadge/SidebarDemoBadge';
import joinClassNames from '@/utils/functions/joinClassNames/joinClassNames';

import SidebarFooter from './SidebarComponents/SidebarFooter';
import SidebarNav from './SidebarComponents/SidebarNav';
import SidebarToggleButton from './SidebarComponents/SidebarToggleButton';
import styles from './styles.module.css';
import useSidebarMenu from './useSidebarMenu';

const DRAWER_ID = 'sidebar-mobile-drawer';

const SidebarMobile = () => {
  const { t } = useTranslation();
  const { isDemo, hasAdvisorView } = useSidebarMenu();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const closeSidebar = () => setIsOpen(false);

  useEffect(() => {
    if (!isOpen) return;

    // A non-modal dialog gets no `cancel` event, so Escape has to be wired up by hand.
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (isOpen) {
      dialog.show();
    } else if (dialog.open) {
      return () => {
        dialog.close();
      };
    }
  }, [isOpen]);

  return (
    <>
      <SidebarToggleButton isOpen={isOpen} onClick={() => setIsOpen(!isOpen)} controlsId={DRAWER_ID} />

      {createPortal(
        <>
          <dialog
            ref={dialogRef}
            id={DRAWER_ID}
            aria-label={t('navigation.mainMenuLabel')}
            className={joinClassNames(
              styles.sidebar,
              styles.sidebarMobile,
              hasAdvisorView && styles.sidebarWithBanner,
              isDemo && styles.sidebarWithDemoBanner,
              !isOpen && styles.closing
            )}
            onAnimationEnd={(e) => {
              if (e.animationName.includes('slideOut')) {
                e.currentTarget.classList.remove(styles.closing);
                e.currentTarget.close();
              }
            }}
            onClose={closeSidebar}
          >
            <div className={styles.sidebarTop}>
              {isDemo && <SidebarDemoBadge />}

              <SidebarNav onNavigate={closeSidebar} />
            </div>

            <SidebarFooter userMenuPlacement="top-start" onNavigate={closeSidebar} />
          </dialog>

          {/* Custo backdrop. With a true ::backdrop the header would be covered, the close button too */}
          <button
            type="button"
            tabIndex={-1}
            aria-hidden="true"
            className={styles.sidebarBackdrop}
            onClick={closeSidebar}
          />
        </>,
        document.body
      )}
    </>
  );
};

export default SidebarMobile;
